import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { LogOut, User, Crown, Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const LangToggle = () => {
  const { i18n } = useTranslation();
  const toggle = () => {
    const next = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(next);
    localStorage.setItem('lang', next);
  };
  return (
    <button
      onClick={toggle}
      className="text-xs font-semibold px-2 py-1 rounded border border-zinc-300 text-zinc-500 hover:text-zinc-900 hover:border-zinc-400 transition-colors"
      aria-label="Switch language"
    >
      {i18n.language === 'fr' ? 'EN' : 'FR'}
    </button>
  );
};

const Header = () => {
  const { user, logout, isPremium } = useAuth();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-100 shadow-sm shadow-zinc-900/5" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-blue-500/30 transition-shadow">
              <span className="text-white text-xs font-black tracking-tight">CV</span>
            </div>
            <span className="font-bold text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="text-zinc-900">JobMatch </span>
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">CV</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {user ? (
              <>
                <Link to="/tableau-de-bord" className="text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="dashboard-link">
                  {t('header.dashboard')}
                </Link>
                <Link to="/tarifs" className="text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="pricing-link">
                  {t('header.pricing')}
                </Link>
                <div className="flex items-center gap-3">
                  {isPremium && (
                    <span className="flex items-center gap-1 text-amber-600 text-sm font-medium">
                      <Crown className="h-4 w-4" />
                      Premium
                    </span>
                  )}
                  <div className="flex items-center gap-2 text-zinc-600">
                    <User className="h-4 w-4" />
                    <span className="text-sm">{user.name}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={logout} className="text-zinc-600 hover:text-zinc-900" data-testid="logout-button">
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link to="/tarifs" className="text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="pricing-link-guest">
                  {t('header.pricing')}
                </Link>
                <Link to="/connexion" data-testid="login-link">
                  <Button variant="outline" className="border-zinc-200 hover:border-zinc-300">
                    {t('header.login')}
                  </Button>
                </Link>
                <Link to="/analyser" data-testid="register-link">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    {t('header.start_free')}
                  </Button>
                </Link>
              </>
            )}
            <LangToggle />
          </nav>

          {/* Mobile: lang toggle + burger */}
          <div className="md:hidden flex items-center gap-2">
            <LangToggle />
            <button className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-button">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-zinc-200">
            <nav className="flex flex-col gap-4">
              {user ? (
                <>
                  <Link to="/tableau-de-bord" className="text-zinc-600 hover:text-zinc-900 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    {t('header.dashboard')}
                  </Link>
                  <Link to="/tarifs" className="text-zinc-600 hover:text-zinc-900 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    {t('header.pricing')}
                  </Link>
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="text-left text-zinc-600 hover:text-zinc-900">
                    {t('header.logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/tarifs" className="text-zinc-600 hover:text-zinc-900 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    {t('header.pricing')}
                  </Link>
                  <Link to="/connexion" className="text-zinc-600 hover:text-zinc-900 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    {t('header.login')}
                  </Link>
                  <Link to="/analyser" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                      {t('header.start_free')}
                    </Button>
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
